import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { formatINR, formatDate } from "@/lib/format";
import { X, Undo2, Clock, ShieldAlert } from "lucide-react";
import { sounds } from "@/lib/sound";

export default function Refunds() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("eligible");
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [e, h] = await Promise.all([
        api.get("/admin/refunds/eligible"),
        api.get("/admin/refunds"),
      ]);
      setItems(e.data);
      setHistory(h.data);
    } catch (err) { console.error("[Refunds] load failed", err); }
  };
  useEffect(() => { load(); }, []);

  const openRefund = (payment) => {
    setActive(payment);
    setReason("");
    setAmount(String(payment.amount || ""));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/admin/refunds/${active.payment_id}`, { reason, amount: parseFloat(amount) });
      toast.success(`Refund issued for ${active.invoice_no}`);
      sounds.refund();
      setActive(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <SectionTitle eyebrow="Compliance" title="Refund Management" />

      <div className="ldc-glass p-4 mb-4 flex items-start gap-3">
        <ShieldAlert size={18} className="text-[#fbce18] mt-0.5" />
        <div className="text-sm text-white/70">
          Refunds are available only within <span className="text-[#fbce18] font-semibold">2 days (48 hours)</span> of a successful payment. After this window, the payment is locked.
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("eligible")} className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] ${tab === "eligible" ? "bg-[#fbce18] text-black" : "border border-white/10 text-white/60"}`} data-testid="refund-tab-eligible">Eligible</button>
        <button onClick={() => setTab("history")} className={`px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] ${tab === "history" ? "bg-[#fbce18] text-black" : "border border-white/10 text-white/60"}`} data-testid="refund-tab-history">History</button>
      </div>

      {tab === "eligible" && (
        <div className="ldc-glass overflow-hidden">
          <div className="overflow-x-auto ldc-scroll">
            <table className="w-full ldc-table" data-testid="refund-eligible-table">
              <thead>
                <tr>
                  <th>Transaction</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Branch</th>
                  <th>Amount</th>
                  <th>Payment Date</th>
                  <th>Refund Window</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (<tr><td colSpan={8} className="text-center py-10 text-white/40">No captured payments within refund window.</td></tr>)}
                {items.map((p) => (
                  <tr key={p.payment_id} data-testid={`refund-row-${p.payment_id}`}>
                    <td className="font-mono text-xs">{p.payment_id}</td>
                    <td className="font-mono text-[#fbce18]">{p.invoice_no || "—"}</td>
                    <td>
                      <div className="text-white/90">{p.customer_name || "—"}</div>
                      <div className="text-[11px] text-white/40">{p.email}</div>
                    </td>
                    <td className="text-sm">{p.branch || "—"}</td>
                    <td className="font-medium">{formatINR(p.amount)}</td>
                    <td className="text-xs text-white/50">{formatDate(p.created_at)}</td>
                    <td>
                      {p.eligible ? (
                        <span className="ldc-badge ldc-badge-pending"><Clock size={10}/> {p.hours_remaining}h left</span>
                      ) : (
                        <span className="ldc-badge ldc-badge-failed">Expired</span>
                      )}
                    </td>
                    <td>
                      {p.eligible ? (
                        <button onClick={() => openRefund(p)} className="ldc-btn-primary px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5" data-testid={`refund-btn-${p.payment_id}`}>
                          <Undo2 size={12}/> Issue Refund
                        </button>
                      ) : (
                        <span className="text-xs text-white/40">Refund Period Expired</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="ldc-glass overflow-hidden">
          <div className="overflow-x-auto ldc-scroll">
            <table className="w-full ldc-table" data-testid="refund-history-table">
              <thead>
                <tr>
                  <th>Refund ID</th>
                  <th>Invoice</th>
                  <th>Payment ID</th>
                  <th>Amount</th>
                  <th>Reason</th>
                  <th>Issued by</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-white/40">No refunds yet.</td></tr>)}
                {history.map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-xs">{r.refund_id}</td>
                    <td className="font-mono text-[#fbce18]">{r.invoice_no || "—"}</td>
                    <td className="font-mono text-xs">{r.payment_id}</td>
                    <td className="font-medium">{formatINR(r.amount)}</td>
                    <td className="text-sm text-white/70">{r.reason || "—"}</td>
                    <td className="text-xs text-white/60">{r.issued_by || "—"}</td>
                    <td className="text-xs text-white/50">{formatDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <form onSubmit={submit} className="ldc-glass p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#fbce18]/80">Confirm</div>
                <h3 className="font-display text-xl">Issue refund</h3>
              </div>
              <button type="button" onClick={() => setActive(null)} className="p-1.5 rounded hover:bg-white/5" data-testid="refund-modal-close"><X size={18}/></button>
            </div>
            <div className="text-sm text-white/70 space-y-1 mb-4">
              <div>Invoice: <span className="font-mono text-[#fbce18]">{active.invoice_no}</span></div>
              <div>Customer: {active.customer_name} · {active.email}</div>
              <div>Captured: {formatDate(active.created_at)}</div>
            </div>
            <div className="space-y-3">
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Amount (max ₹ {active.amount})</div>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="refund-amount" />
              </label>
              <label className="block">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">Reason</div>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid="refund-reason" placeholder="Duplicate charge, service not delivered, etc." />
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setActive(null)} className="px-4 py-2 rounded-md border border-white/10 text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="refund-confirm">
                {busy ? "Processing…" : "Confirm refund"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
